
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";
import type { UserProfile, ExternalEvent, FavoriteGroupRide, FavoriteRace } from "../types";

export const userService = {
  /**
   * Fetches the user profile from Firestore.
   * Returns null if no profile exists yet.
   */
  getUserProfile: async (uid: string): Promise<UserProfile | null> => {
    try {
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // Normalize data to ensure application consistency
        // Specifically handle the case-sensitive 'IsAdmin' vs 'isAdmin' field
        return {
            ...data,
            isAdmin: data.isAdmin || data.IsAdmin || false
        } as UserProfile;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      throw error;
    }
  },

  /**
   * Creates or updates the user profile in Firestore.
   */
  updateUserProfile: async (uid: string, data: Partial<UserProfile>): Promise<void> => {
    try {
      const docRef = doc(db, "users", uid);
      const updates: Record<string, any> = {
        ...data,
        uid,
        updatedAt: Date.now()
      };

      // Automatically sync teamId as slug of teamName for single source of truth
      const rawTeamName = (data.teamName || (data as any).team_name || (data as any).team || "").trim();
      if (rawTeamName) {
        updates.teamName = rawTeamName;
        if (rawTeamName.toLowerCase() !== "solo unaffiliated rider") {
          updates.teamId = rawTeamName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_+|_+$)/g, "");
        } else {
          updates.teamId = "solo_unaffiliated";
        }
      }

      await setDoc(docRef, updates, { merge: true });
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  },

  /**
   * Adds an external event to the user's planned events list.
   */
  addPlannedEvent: async (uid: string, event: ExternalEvent): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          await updateDoc(docRef, {
              plannedEvents: arrayUnion(event)
          });
      } catch (error) {
          console.error("Error adding planned event:", error);
          throw error;
      }
  },

  /**
   * Removes an event from the user's planned events list.
   */
  removePlannedEvent: async (uid: string, event: ExternalEvent): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          await updateDoc(docRef, {
              plannedEvents: arrayRemove(event)
          });
      } catch (error) {
          console.error("Error removing planned event:", error);
          throw error;
      }
  },

  /**
   * Adds a group ride to the user's list of favorited group rides.
   */
  addFavoriteGroupRide: async (uid: string, ride: FavoriteGroupRide): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          await updateDoc(docRef, {
              favoriteGroupRides: arrayUnion(ride)
          });
      } catch (error) {
          console.error("Error adding favorite group ride:", error);
          throw error;
      }
  },

  /**
   * Removes a group ride from the user's list of favorited group rides.
   * Matches by ride ID since arrayRemove requires an exact object match.
   * We fetch the current array, filter by ID, then use set/merge to update.
   */
  removeFavoriteGroupRide: async (uid: string, rideId: string): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
              const data = docSnap.data();
              const existing: FavoriteGroupRide[] = data.favoriteGroupRides || [];
              const updated = existing.filter(r => r.id !== rideId);
              await updateDoc(docRef, { favoriteGroupRides: updated });
          }
      } catch (error) {
          console.error("Error removing favorite group ride:", error);
          throw error;
      }
  },

  /**
   * Adds a community race to the user's list of favorited races.
   */
  addFavoriteRace: async (uid: string, race: FavoriteRace): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          await updateDoc(docRef, {
              favoriteRaces: arrayUnion(race)
          });
      } catch (error) {
          console.error("Error adding favorite race:", error);
          throw error;
      }
  },

  /**
   * Removes a community race from the user's list of favorited races.
   */
  removeFavoriteRace: async (uid: string, raceId: string): Promise<void> => {
      try {
          const docRef = doc(db, "users", uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
              const data = docSnap.data();
              const existing: FavoriteRace[] = data.favoriteRaces || [];
              const updated = existing.filter(r => r.id !== raceId);
              await updateDoc(docRef, { favoriteRaces: updated });
          }
      } catch (error) {
          console.error("Error removing favorite race:", error);
          throw error;
      }
  }
};
